"use client";

import type React from "react";

import { clsx } from "clsx";
import * as m from "motion/react-m";
import { pxToRem } from "@ui/lib/utils";
import { useEffect, useId, useRef, useState } from "react";
import {
  PILL_SIZES,
  PILL_RING_CLASS,
  PILL_COLORS,
  getPillHoverInset,
  getPillItemPadding,
  type PillSize,
  type PillSurface,
  type PillInactiveTextColor,
} from "./pill-switcher-styles";

export interface SwitcherOption {
  icon?: React.ReactNode;
  id: string;
  label: string;
}

export interface SwitcherButtonProps {
  className?: string;
  onSelect: (index: number) => void;
  options: SwitcherOption[];
  selectedIndex: number;
  disabled?: boolean;
  size?: PillSize;
  showRing?: boolean;
  surface?: PillSurface;
  inactiveTextColor?: PillInactiveTextColor;
}

export function SwitcherButton({
  options,
  selectedIndex,
  onSelect,
  className,
  disabled,
  size = "lg",
  showRing = true,
  surface = "white",
  inactiveTextColor = "default",
}: SwitcherButtonProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hasOverflow, setHasOverflow] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const { heightClass, padding, textClass } = PILL_SIZES[size];
  const hoverInset = getPillHoverInset(showRing);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const { scrollWidth, clientWidth } = containerRef.current;
        setHasOverflow(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [options]);

  if (options.length <= 1) return null;

  return (
    <div className={clsx("tw-relative tw-flex tw-max-w-full tw-justify-center tw-py-2", className)}>
      <div
        className={clsx(
          "tw-relative tw-flex tw-w-full tw-rounded-full",
          showRing && PILL_RING_CLASS,
          hasOverflow ? "tw-overflow-hidden" : "tw-overflow-visible",
        )}
      >
        <div
          className={clsx(
            "tw-scrollbar-none tw-flex tw-w-full",
            hasOverflow ? "tw-overflow-x-auto" : "tw-overflow-x-visible",
          )}
          ref={containerRef}
        >
          {options.map((option, index) => {
            const isFirst = index === 0;
            const isLast = index === options.length - 1;
            const isSelected = selectedIndex === index;
            const isHovered = hoveredIndex === index;

            return (
              <button
                className={clsx(
                  "tw-isolate tw-relative tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-shrink-0 tw-outline-none focus-visible:tw-outline-focus tw-rounded-full",
                  heightClass,
                )}
                style={getPillItemPadding(padding, isFirst, isLast)}
                key={option.id}
                onClick={() => onSelect(index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                disabled={disabled}
              >
                {isSelected && (
                  <m.div
                    className='tw-absolute tw-inset-0 -tw-z-10 tw-rounded-full'
                    layoutId={`switcher-highlight-${id}`}
                    initial={false}
                    animate={{
                      background: PILL_COLORS.selectedBg,
                      boxShadow: PILL_COLORS.selectedShadow,
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                {!isSelected && (
                  <m.div
                    className='tw-absolute -tw-z-10 tw-rounded-full'
                    style={{
                      top: pxToRem(hoverInset),
                      bottom: pxToRem(hoverInset),
                      left: isFirst ? pxToRem(hoverInset) : pxToRem(hoverInset + 1),
                      right: isLast ? pxToRem(hoverInset) : pxToRem(hoverInset + 1),
                    }}
                    initial={false}
                    animate={{
                      background: isHovered ? PILL_COLORS.hoverBg[surface] : PILL_COLORS.transparentBg[surface],
                    }}
                  />
                )}
                <m.div
                  className={clsx("tw-relative tw-flex tw-items-center tw-gap-1.5", textClass)}
                  initial={false}
                  animate={{
                    color: isSelected
                      ? PILL_COLORS.active
                      : isHovered && surface !== "dark"
                        ? PILL_COLORS.active
                        : PILL_COLORS.inactive[inactiveTextColor],
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  {option.icon}
                  {option.label}
                </m.div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
